// src/components/PatchNotesModal.js
// Shows the latest patch note once per browser after it ships. Tracked in
// localStorage (not the Firestore profile) deliberately — this should work
// identically for signed-in and anonymous visitors, and doesn't need to
// sync across devices; seeing it again on a second device is fine.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, X } from 'lucide-react';
import { patchNotes } from '../data/patchNotes';

const STORAGE_KEY = 'elpis_last_seen_patch';
const INK = '#1e1b4b';
const MUTE = '#6b6892';
const GOLD = '#f97316';
const CORAL = '#ff6b4a';

export default function PatchNotesModal() {
  const [visible, setVisible] = useState(false);
  const latest = patchNotes[0];

  useEffect(() => {
    if (!latest) return;
    let lastSeen = null;
    try { lastSeen = window.localStorage.getItem(STORAGE_KEY); } catch { /* localStorage unavailable — just skip */ }
    if (lastSeen !== latest.version) setVisible(true);
  }, [latest]);

  function dismiss() {
    try { window.localStorage.setItem(STORAGE_KEY, latest.version); } catch { /* ignore */ }
    setVisible(false);
  }

  if (!visible || !latest) return null;

  return (
    <div
      onClick={dismiss}
      style={{
        position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(30,27,75,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div onClick={e => e.stopPropagation()} style={{
        width: '100%', maxWidth: 400,
        background: 'linear-gradient(165deg, rgba(255,255,255,0.95), rgba(255,246,234,0.88))',
        backdropFilter: 'blur(18px) saturate(180%)', WebkitBackdropFilter: 'blur(18px) saturate(180%)',
        border: '1px solid rgba(255,255,255,0.6)', borderRadius: 20, padding: 28,
        boxShadow: '0 1px 0 rgba(255,255,255,0.8) inset, 0 30px 60px -24px rgba(30,27,75,0.35)',
        position: 'relative',
      }}>
        <button
          onClick={dismiss}
          style={{
            all: 'unset', cursor: 'pointer', position: 'absolute', top: 16, right: 16,
            color: MUTE, display: 'flex', padding: 4,
          }}
          aria-label="Close"
        >
          <X size={16} />
        </button>

        <div style={{
          width: 40, height: 40, borderRadius: 12,
          background: `linear-gradient(135deg, ${GOLD}, ${CORAL})`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14,
        }}>
          <Sparkles size={19} color="#fff" />
        </div>

        <h2 style={{ fontSize: 19, fontWeight: 800, color: INK, marginBottom: 2 }}>{latest.title}</h2>
        <p style={{ fontSize: 12, color: MUTE, marginBottom: 18 }}>{latest.date}</p>

        <ul style={{ listStyle: 'none', margin: 0, padding: 0, marginBottom: 22 }}>
          {latest.items.map((item, i) => (
            <li key={i} style={{
              display: 'flex', gap: 10, fontSize: 13.5, color: '#57527e', lineHeight: 1.5,
              marginBottom: 10, alignItems: 'flex-start',
            }}>
              <span style={{
                width: 5, height: 5, borderRadius: '50%', background: GOLD,
                marginTop: 7, flexShrink: 0,
              }} />
              {item}
            </li>
          ))}
        </ul>

        <button
          onClick={dismiss}
          style={{
            width: '100%', padding: '12px 0', borderRadius: 10, border: 'none', cursor: 'pointer',
            background: `linear-gradient(135deg, ${GOLD}, ${CORAL})`, color: '#fff',
            fontWeight: 700, fontSize: 14,
          }}
        >
          Got it
        </button>
        <Link
          to="/changelog"
          onClick={dismiss}
          style={{ display: 'block', textAlign: 'center', fontSize: 12, color: MUTE, marginTop: 12, textDecoration: 'none' }}
        >
          See full changelog
        </Link>
      </div>
    </div>
  );
}
